/**
 * This file contains the logic for pushing GA4 events into the datalayer.
 *
 * The source data gets handled by json--datalayer.liquid, json--datalayer-checkout.liquid
 * and json--datalayer-select-items.liquid files.
 */

/**
 * @desc Handle the Events that needs to be fired on the load of the page.
 *      The variable pageName is declared on json--datalayer.liquid file.
 */
export const pushOnloadEvents = () => {
    switch (pageName) {
        case "cart":
            pushDataLayerEvent("view_cart")
        case "product":
            pushDataLayerEvent("view_item")
        default:
            pushDataLayerEvent("page_view")
            break;
    }
}

/**
 * @desc Handle the checkout Event depending of the checkout step.
 *      Calls checkout handling functions inside json--datalayer-checkout.liquid file
 */
export const handleCheckoutEvents = () => {
    let eventBody = ""
    switch (true) {
        case !url.href.includes('&step=shipping_method') && !url.href.includes('&step=payment_method') && !url.href.includes('thank_you') && !url.href.includes('processing'):
            eventBody = beginCheckoutEvent();
            pushDataLayerEvent("begin_checkout", false, eventBody);
            break;
        case url.href.includes('&step=shipping_method'):
            addShippingInfoEvent()
            break;
        case url.href.includes('&step=payment_method'):
            addPaymentInfoEvent()
            break;
        case url.href.includes('thank_you'):
            eventBody = purchaseEvent();
            pushDataLayerEvent("purchase", false, eventBody);
            break;
    }
}

/**
 * @desc Gets the cart before triggering the GA4 cart events to have the updated version of the cart.
 */
const fetchCart = async () => {
    try {
        let response = await fetch(window.Shopify.routes.root + 'cart.js');
        if (!response.ok) {
            console.error('Error fetching cart, error code:', response.status);
            return null;
        }

        const cartContents = await response.json();

        // Get extended product information
        const extendedDataResponse = await fetch(`${window.Shopify.routes.root}cart?view=json-data-mini`);
        if (!extendedDataResponse.ok) {
            console.error('Error fetching extended cart data, error code:', extendedDataResponse.status);
            return cartContents;
        }

        // Create faster lookups for extended item data
        const extendedCartContentsMap = {};
        const extendedCartContents = await extendedDataResponse.json();
        extendedCartContents.products.forEach((product) => {
            extendedCartContentsMap[product.id] = product;
        });

        // Add additional product information to cart items
        cartContents.items.forEach((item) => {
            const productId = item.product_id;
            if (extendedCartContentsMap[productId]) {
                const gaTrackingName = extendedCartContentsMap[productId].ga_tracking_name;
                if (gaTrackingName) {
                    // eslint-disable-next-line no-param-reassign
                    item.ga_tracking_name = gaTrackingName;
                }
            }
        });

        return cartContents;
    } catch (error) {
        console.error('Error fetching cart data:', error);
        return null;
    }
};

/**
 * @desc Builds the event schema before pushing it into the datalayer.
 */
const buildEventSchema = async (event, variant, extraParams = null) => {
    let eventBody;

    if ([
      "view_cart",
      "add_to_cart",
      "remove_from_cart",
      "cart_quantity_increase",
      "cart_quantity_decrease"
    ].includes(event)) {
        const cart = await fetchCart();

        /**
         * Check if the fetched cart is available, if not it will pass an empty string and the event
         * will use the global Object cart.
         */
        eventBody = buildEventsJSON(event, variant, cart);
    } else {
        eventBody = buildEventsJSON(event, variant, "", extraParams);
    }

    return eventBody || {};
};

/**
 * @desc Pushes the specified event to the dataLayer with the variant shaped
 *     into the specified schema from buildEventSchema().
 * @param {string} event
 * @param {boolean} eventOnly
 * @param {mixed} variant
 */
export const pushDataLayerEvent = (event, eventOnly = false, variant = "", extraParams = null) => {
    window.dataLayer = window.dataLayer || [];

    // Validate event parameter
    if (!event || typeof event !== "string") {
        console.error("Invalid event provided", { event });
        return;
    }

    // Use case for events without dimensions
    if (eventOnly) {
        window.dataLayer.push({ event });
        return;
      }

    buildEventSchema(event, variant, extraParams).then((eventData) => {
        // Check if the returned schema is correct.
        if (!Object.keys(eventData).length) {
            console.error(
                "postBody is empty: No matching case for the provided event.",
                { event }
            );
            return;
        }

        if (eventData) {
            window.dataLayer.push(eventData)
        }
    })
}
